/* eslint-disable max-len */
import React, { useState, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { modeUIContext, setIsEditingForm, showErrorNotification, showSuccessNotification } from '@moderepo/mode-ui-state';
import clsx from 'clsx';
import produce, { Draft } from 'immer';
import { selectProjectById, ritzDataContext, selectAgentLoginInfo } from '@moderepo/mode-data-state-ritz';
import { BaseInfoCompProps, capitalizeAll, getErrorMessage, SelectInputOption,
    SELECT_INPUT_OPTION_DIVIDER, SELECT_INPUT_OPTION_DIVIDER_TYPE } from '@moderepo/mode-app-components';
import { AppAPI, ApiError } from '@moderepo/mode-apis';
import { RitzWebSocketFactory, ModeWebSocketEvent, WebSocketObserver, isModeWebSocketEvent } from '@moderepo/mode-internal-apis';
import { SendDeviceCommandComp, SendDeviceCommandCompInputs, SendDeviceCommandCompProps } from '../../components/devices';
import { isObjectLiteral, isValidSettingStringValue } from '../../utils/utils';
import { useRitzTranslation } from '../../utils/customHooks';
import { useSelectDevice } from '../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../utils/logger';


interface InputErrors {
    predefinedCommand?: string | undefined;
    customCommand?: string | undefined;
    parameters?: string | undefined;
}


// We will need to add a CUSTOM COMMAND option to the list of Device Command Options and we will use this as the custom command
// option value. When the user select one of the option, we can compare the selected option value with this value to see if
// the user selected the custom command option or one of the predefined options
const CUSTOM_COMMAND_OPTION = 'Custom_Device_Command_Option';



/**
 * The types of command. The structure of the data sent to the device will depend on this type
 */
export enum DeviceCommandType {
    TRADITIONAL = 'traditional',
    PERIPHERAL = 'peripheral',
}


/**
 * Predefined DeviceCommand configuration. Each device supports different set of commands therefore the UI doesn't know what commands
 * are allowed. Therefore, if the parent container knows about the device and what commands the device support, it can pass
 * in the set of command configuration for this component to use.
 */
export interface DeviceCommandSettings {
    readonly type: DeviceCommandType;

    // The actual command to be send to the device
    readonly command: string;

    // The user friendly display name of the command. This can be a translation key. If this key does not exist in the translation,
    // the component will display the key as is.
    readonly displayNameKey: string;

    // Whether or not this command require parameters. If true, the component will give the user the input field to enter the
    // parameters. The parameters must be entered as JSON string.
    readonly hasParameters?: boolean | undefined;

    // The default parameters to be sent with the command.
    readonly defaultParameters?: object | undefined;

    // Whether or not this command has response. If true, the component will try to wait for the device's response and show the response
    readonly hasResponse?: boolean | undefined;

    // The response for commands that hasResponse will come back as EVENTS from web socket. The event objects have an attribute call "eventData"
    // and the structure of this eventData can be anything depending on the Gateway implementation. Therefore, this component does not know
    // how to use this eventData and we will need additional settings to tell the component how to use the eventData.

    // The name of the field in the eventData object where the result of the command should be retrieved from. If this is not provided, we will show
    // the entire eventData. This is only required if 'hasResponse' is true
    readonly responseResultFieldName?: string | undefined;

    /**
     * Because events come from web socket can be triggered by anything, not just from sending a command to a device. Therefore, this component
     * won't know which event is the response for the command it just sent. To solve this issue, the gateway can provide some data inside the
     * eventData so that the front end can use that to determine if the eventData is the response for the command. However, we don't have a
     * rule/standard on what the gateway will add to the eventData object, it is all depends on the project and gateway implementation. We can't
     * assume or expect the eventData to have some specific info that we can use. Therefore, we can use this settings to tell the component
     * what attribute to look for in the eventData. 1 thing the gateway can add to the eventData is the command that front end sent so that the
     * front end can use it to match with command. But we don't know the name of the field the gateway will be using. It would be called "action"
     * or it could be called "command", etc... So use this attribute "responseActionFieldName" to tell this component which field to check
     */
    readonly responseActionFieldName?: string | undefined;

    /**
     * Here is an example how the "responseResultFieldName" and "responseActionFieldName" can be configured
     * E.g. Lets say we send a command 'Reset_Device' to the device and the gateway responses to the command with an Event and the eventData
     * for the response is like this
     *
     *      eventData: {
     *          action: "Reset_Device",
     *          result: {...}
     *      }
     *
     * This means the gateway will add the command that was sent, 'Reset_Device', to the eventData so that the front end can use this to determine
     * that the event object is the response for the command we just sent and the field name the gateway will use is called "action". And the
     * gateway will pass the result of the command under the field name "result". Therefore, we can configure this component like this
     *
     *      deviceCommandSettings: {
     *          command: 'Reset_Device',
     *          hasParameters: false,
     *          hasResponse: true,
     *          responseResultFieldName: 'result',
     *          responseActionFieldName: 'action'
     *      }
     */
}


/**
 * For PERIPHERAL device type, we also need "action" and "destination" therefore we will define an interface for
 * PeripheralDeviceCommandSettings which extends the DeviceCommandSettings
 */
export interface PeripheralDeviceCommandSettings extends DeviceCommandSettings {
    readonly type: DeviceCommandType.PERIPHERAL;

    // The action to send to the backend e.g. "peripheralModuleAction"
    readonly action: string;

    // This is a template because the UI needs to add deviceId to the string. Use {{deviceId}} in the string as placeholder.
    // For example "ug::JetsonAGXXavier:{{deviceId}}". The frontend will replace {{deviceId}} with the actual deviceId
    readonly destinationTemplate: string;
}


/**
 * Type guard function to check if an obj is a PeripheralDeviceCommandSettings
 */
export const isPeripheralDeviceCommandSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is PeripheralDeviceCommandSettings => {
    const settings = obj as PeripheralDeviceCommandSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (settings.type !== DeviceCommandType.PERIPHERAL) {
        return errorLogger?.logError({
            message: `"type" must be "${DeviceCommandType.PERIPHERAL}"`,
            value  : settings.type,
            path   : [...configPath, 'type'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.action, true)) {
        return errorLogger?.logError({
            message: '"action" is required and must be a non-empty string"',
            value  : settings.action,
            path   : [...configPath, 'action'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.destinationTemplate, true)) {
        return errorLogger?.logError({
            message: '"destinationTemplate" is required and must be a non-empty string"',
            value  : settings.destinationTemplate,
            path   : [...configPath, 'destinationTemplate'],
        }) ?? false;
    }

    return true;
};


/**
 * The object used for storing all the info we need for the commandWaitingForResponse.
 */
interface CommandInfo {
    readonly deviceId: number;
    readonly action: string;
    readonly commandType: string;
    readonly command: string;
    readonly commandSettings?: DeviceCommandSettings | undefined,
}


/**
 * Check if the given event object is the response for the commandWaitingForResponse
 */
const isEventObjectTheResponseForTheLastCommand = (event: ModeWebSocketEvent, commandWaitingForResponse: CommandInfo | undefined): boolean => {

    // There is no commandWaitingForResponse we can ignore the event
    if (!commandWaitingForResponse) {
        return false;
    }

    // the deviceIds don't match which mean this event is for something else.
    if (event.originDeviceId !== commandWaitingForResponse.deviceId) {
        return false;
    }

    // If the event is a response for a command, event.eventType will be the action. If the eventType and action don't match then the
    // event is probably for something else
    if (event.eventType !== commandWaitingForResponse.action) {
        return false;
    }

    // Now we look inside the eventData. eventData structure is unknown so we need to use the command settings to determine if the event is
    // for the command we just sent. We only need to do this if 'eventData' is an OBJECT and we have 'responseActionFieldName' setting.
    const eventData = event.eventData;
    if (isObjectLiteral(eventData) && commandWaitingForResponse.commandSettings?.responseActionFieldName) {
        const actionField = commandWaitingForResponse.commandSettings.responseActionFieldName;

        // See if the eventData's value for the given actionField is not the same as the command we sent recently then we can assume the
        // event is not for the command.
        if (eventData[actionField] !== commandWaitingForResponse.command) {
            return false;
        }
    }

    return true;
};



/**
 * The props required for the SendDeviceCommandContainer component.
 */
export interface SendDeviceCommandContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly deviceId: number;

    // The list of predefined commands that the user can select from instead of entering a command manually.
    readonly predefinedCommands?: readonly (SELECT_INPUT_OPTION_DIVIDER_TYPE | DeviceCommandSettings)[] | undefined;

    // By default, we will allow the user to enter a command manually incase the set of predefinedCommand does not have the command
    // that the need. This option is for disabling the custom command option and only allow the user to choose one of the predefined options.
    readonly disableCustomCommand?: boolean | undefined;

    // Optional props to pass to SendDeviceCommandComp
    readonly infoCompProps?: {
        readonly className?: BaseInfoCompProps['className'] | undefined;
        readonly formProps?: BaseInfoCompProps['formProps'] | undefined;
    } | undefined;

    // On sent device completed handler. The parent container can listen to this event and do what it needs to do when the user
    // is done sending command to the device.
    readonly onCompleted?: (()=> void) | undefined;
}



/**
 * This is the component that provide the UI for the user to send command to a device. This component contains the logic and it will
 * use the SendDeviceCommandComp dumb component for the UI
 */
export const SendDeviceCommandContainer = (props: SendDeviceCommandContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const selectedDevice = useSelectDevice(props.projectId, props.deviceId);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [inputErrors, setInputErrors] = useState<InputErrors>({});
    const [currentInputs, setCurrentInputs] = useState<SendDeviceCommandCompInputs>({ });
    const agentLoginInfo = selectAgentLoginInfo(dataState);



    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * Automatically set editing mode to true because this component will always be in edit mode
     */
    useEffect(() => {
        onChangeEditingMode(true);
    }, [onChangeEditingMode]);


    // The state that keeps track of the command that is waiting for response. This object contains all the info we need to compare with the
    // events from the web socket to determine if the an event is a response for a command we sent.
    const commandWaitingForResponseRef = useRef<CommandInfo | undefined>();

    // The state that store the last command's result
    const [lastCommandResult, setLastCommandResult] = useState<string | undefined>();


    /**
     * The web socket observer which get called each time an event is sent to the web socket connection. This observer will check if
     * the new event is a response to the commandWaitingForResponse and handle it accordingly if it is.
     */
    const websocketObserver = useMemo((): WebSocketObserver => {
        return (event: ModeWebSocketEvent | unknown) => {

            // Check if the event we received from the web socket is a response for the last command we sent. If it is then get the
            // response result from the event object
            if (isModeWebSocketEvent(event) && isEventObjectTheResponseForTheLastCommand(event, commandWaitingForResponseRef.current)) {

                // Get the result from the event.eventData based on the command's responseResultFieldName settings. If there is
                // no 'responseResultFieldName' settings then use the entire event.eventData as the result.
                const resultField = commandWaitingForResponseRef.current?.commandSettings?.responseResultFieldName;
                const result = resultField && isObjectLiteral(event.eventData) && event.eventData[resultField] !== undefined
                    ? event.eventData[resultField] : event.eventData;

                setLastCommandResult(JSON.stringify(result, undefined, 4));


                // We have received the response for the command we sent. We can clear the lastCommandInfoRef now so that the code
                // doesn't try to check other events from the web socket.
                commandWaitingForResponseRef.current = undefined;
            }
        };
    }, []);



    /**
     * On component loads, we will create a web socket connection and continuously listen to events from the socket so that we
     * can get results for commands the user sent.
     */
    useEffect(() => {

        // Create a WebSocket connection once we have fetched DEVICE and AgentLoginInfo
        const wsObservable = selectedDevice?.homeId && agentLoginInfo?.xsrfGuard
            ? RitzWebSocketFactory.getInstance().createObservableWebSocket(selectedDevice.homeId, {
                authToken: agentLoginInfo?.xsrfGuard,
            }) : undefined;

        wsObservable?.subscribe(websocketObserver);

        return () => {
            wsObservable?.destroy();
        };
    }, [agentLoginInfo?.xsrfGuard, selectedDevice?.homeId, websocketObserver]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setInputErrors({});

        onChangeEditingMode(false);

        if (props.onCompleted) {
            props.onCompleted();
        }
    }, [onChangeEditingMode, props]);



    /**
     * On form input change, we will clear the error message for that field and store the new input value in currentInputs object.
     */
    const onSendCommandCompFormInputChange = useCallback((fieldName: string, value: string) => {
        setInputErrors(produce(inputErrors, (draft: any) => {
            draft[fieldName] = null;
        }));

        setCurrentInputs((currentInputs) => {
            return produce(currentInputs, (draft: Draft<SendDeviceCommandCompInputs>) => {
                draft[fieldName] = value;

                // If a different predefinedCommand is selected then we need to reset the "parameters" and "customCommand" input.
                if (fieldName === 'predefinedCommand') {
                    // Find the command settings for the selected predefined command
                    const match = props.predefinedCommands?.find((commandSettings) => {
                        return typeof commandSettings === 'object' && commandSettings.command === value;
                    });

                    const predefinedCommandSettings = typeof match === 'object' ? match : undefined;

                    // Check if the selected predefined command contains "defaultParameters". If it does then initialize the
                    // parameters input with the "defaultParameters". Else, reset it to undefined.
                    if (predefinedCommandSettings?.hasParameters && predefinedCommandSettings?.defaultParameters) {
                        draft.parameters = JSON.stringify(predefinedCommandSettings.defaultParameters, undefined, 4);
                    } else {
                        draft.parameters = undefined;
                    }

                    draft.customCommand = undefined;
                }
            });
        });

        // If the user changed the input then we can clear the previous result and commandWaitingForResponseRef
        setLastCommandResult(undefined);
        commandWaitingForResponseRef.current = undefined;
    }, [inputErrors, props.predefinedCommands]);


    /**
     * Try to convert the parameters input value into a JSON object and reformat it. Show error if value is an invalid JSON
     */
    const onFormatParametersJSON = useCallback(() => {
        if (currentInputs.parameters) {
            try {
                const formattedValue = JSON.stringify(JSON.parse(currentInputs.parameters), null, 4);
                setInputErrors(produce(inputErrors, (draft: any) => {
                    draft.parameters = null;
                }));
                setCurrentInputs((currentInputs) => {
                    return produce(currentInputs, (draft: Draft<SendDeviceCommandCompInputs>) => {
                        draft.parameters = formattedValue;
                    });
                });
            } catch (error) {
                setInputErrors(produce(inputErrors, (draft: any) => {
                    draft.parameters = (error as Error).message;
                }));
            }
        }
    }, [currentInputs.parameters, inputErrors]);



    const onCancelEdit = useCallback(() => {
        closeEditing();
    }, [closeEditing]);



    const sendCommand = useCallback(async (
        command: string, parameters: object | undefined, predefinedCommandSettings: DeviceCommandSettings | undefined, hasResponse: boolean | undefined,
    ) => {
        try {
            const isPeripheralDeviceCommand = isPeripheralDeviceCommandSettings(predefinedCommandSettings);

            const payload = isPeripheralDeviceCommand
                // This is the payload structure required for Peripheral command type
                ? {
                    action    : predefinedCommandSettings.action,
                    parameters: {
                        destination: predefinedCommandSettings.destinationTemplate.replaceAll('{{deviceId}}', props.deviceId.toString()),
                        action     : command,
                        parameters,
                    },
                }
                // This is the payload structure required for traditional command type
                : {
                    action: command,
                    parameters,
                };


            // Before sending the command, save all the parameters so that when we get the response from the web socket, we can try to match
            // the response with these params to determine if the response is for this command we are about to send. We only need to do this if
            // the event we are about to send has a response
            if (hasResponse) {
                commandWaitingForResponseRef.current = {
                    deviceId       : props.deviceId,
                    commandType    : isPeripheralDeviceCommand ? predefinedCommandSettings.action : 'modeCommand',
                    action         : isPeripheralDeviceCommand ? predefinedCommandSettings.action : command,
                    command,
                    commandSettings: predefinedCommandSettings,
                };

                // Temporary set the result to "Loading..." until we receive the result from the web socket
                setLastCommandResult(trans('label.waiting_for_command_result'));
            } else {
                // If the command doesn't need response then clear the commandWaitingForResponseRef
                commandWaitingForResponseRef.current = undefined;
                setLastCommandResult(undefined);
            }


            await AppAPI.getInstance().sendCommandToDevice(props.deviceId, payload);
            uiDispatch(showSuccessNotification(trans('notif.command_sent_successfully')));

        } catch (error) {
            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));

            // Send command failed so we won't need to wait for result.
            commandWaitingForResponseRef.current = undefined;
            setLastCommandResult(undefined);
        }
    }, [props.deviceId, trans, uiDispatch]);


    /**
     * Handler for when the user click Save input from the member form
     */
    const onSendDeviceCommandFormSubmit = useCallback((inputs: SendDeviceCommandCompInputs) => {
        const { predefinedCommand, customCommand, parameters, hasResponse } = inputs;

        const newInputErrors: InputErrors = {};

        // predefinedCommand input is required
        if (!predefinedCommand) {
            newInputErrors.predefinedCommand = 'error.input.generic.empty';
        }

        // If the selected predefinedCommand is the CUSTOM_COMMAND_OPTION then customCommand is required
        if (predefinedCommand === CUSTOM_COMMAND_OPTION && !customCommand) {
            newInputErrors.customCommand = 'error.input.generic.empty';
        }

        // If the selected predefinedCommand is one of the commands that require parameters then need to validate parameters
        // If the customCommand is used, the parameters is OPTIONAL BUT if it is provided then it must be a valid JSON
        const match = props.predefinedCommands?.find((commandSettings) => {
            return typeof commandSettings === 'object' && commandSettings.command === predefinedCommand;
        });

        const predefinedCommandSettings = typeof match === 'object' ? match : undefined;

        // We need to validate the parameters if the selected predefinedCommand is one of the command that require parameters
        // or the command is custom and parameters is provided.
        const hasParameters = predefinedCommandSettings?.hasParameters || (predefinedCommand === CUSTOM_COMMAND_OPTION && parameters !== undefined);

        // Whether or not to expect a response data from the backend. This is true if the commandSettings.hasResponse is set to true OR
        // if the user selected a CUSTOM command AND checked the "Has Response" checkbox,
        const hasResponseData = predefinedCommandSettings?.hasResponse || (predefinedCommand === CUSTOM_COMMAND_OPTION && hasResponse);


        if (hasParameters) {
            if (!parameters) {
                newInputErrors.parameters = 'error.input.generic.empty';
            } else {
                // Try to parse the parameters string to JSON. If it fail to parse, set error for the parameters input
                try {
                    const paramObject = JSON.parse(parameters);

                    // Backend expects parameters to be an Object so we need to make sure it is an object, not other value
                    // type e.g. Array, string, number, etc.
                    if (!isObjectLiteral(paramObject)) {
                        newInputErrors.parameters = 'error.input.json.invalid';
                    }
                } catch (error) {
                    newInputErrors.parameters = 'error.input.json.invalid';
                }
            }
        }


        // Update the input errors state with the new errors
        setInputErrors(newInputErrors);

        // Check if there is 1 or more errors. If there are no error then make API call to send the command
        const hasInputErrors = Object.values(newInputErrors).some((error) => {
            return error !== undefined;
        });


        const command = predefinedCommand !== CUSTOM_COMMAND_OPTION ? predefinedCommand : customCommand;
        if (!hasInputErrors && command) {
            sendCommand(command, hasParameters && parameters ? JSON.parse(parameters) : undefined, predefinedCommandSettings, hasResponseData);
        }
    }, [props.predefinedCommands, sendCommand]);



    /**
     * If "predefinedCommands" is provided then create an Array of Select Input Options for the commands. These will be passed to the
     * SendDeviceCommandComp's "command" input field to be shown in the Select control.
     * Also, add an option for the user to enter custom command incase the list of predefined commands does not have a command that the user
     * wants to send
     */
    const commandOptions = useMemo((): readonly (SelectInputOption<string> | SELECT_INPUT_OPTION_DIVIDER_TYPE)[] => {
        if (props.predefinedCommands) {

            // Create an array of options for the predefined commands
            const commandOptions = props.predefinedCommands.map((commandSettings): SelectInputOption<string> | SELECT_INPUT_OPTION_DIVIDER_TYPE => {
                if (commandSettings === SELECT_INPUT_OPTION_DIVIDER) {
                    return commandSettings;
                }
                return {
                    value: commandSettings.command,
                    label: trans(commandSettings.displayNameKey),
                };
            });

            // Add an option for custom command IF disableCustomCommand is not set to true
            if (!props.disableCustomCommand) {
                commandOptions.push(SELECT_INPUT_OPTION_DIVIDER);
                commandOptions.push({
                    label: capitalizeAll(trans('label.custom_device_command_option')),
                    value: CUSTOM_COMMAND_OPTION,
                });
            }

            return commandOptions;
        }

        return [];
    }, [props.disableCustomCommand, props.predefinedCommands, trans]);



    /**
     * Create Input settings for the SendDeviceCommandContainer and cache it so that it doesn't get created on every re-render
     */
    const inputsSettings = useMemo((): SendDeviceCommandCompProps['fields'] => {

        const selectedPredefinedCommand = currentInputs.predefinedCommand;

        // Find the command settings object for the currently selected predefined command if it is NOT a CUSTOM_COMMAND
        const match = selectedPredefinedCommand !== CUSTOM_COMMAND_OPTION ? props.predefinedCommands?.find((commandSettings) => {
            return typeof commandSettings === 'object' && commandSettings.command === selectedPredefinedCommand;
        }) : undefined;

        const predefinedCommandSettings = typeof match === 'object' ? match : undefined;

        // Show "parameters" input if the selected predefined command has "hasParameters" set to true or if the user selected CUSTOM command.
        const hasParametersInput = predefinedCommandSettings?.hasParameters || selectedPredefinedCommand === CUSTOM_COMMAND_OPTION;

        return {
            predefinedCommand: {
                label   : capitalizeAll(trans('label.command')),
                options : commandOptions,
                editable: true,
                required: true,
                value   : currentInputs.predefinedCommand,
                error   : inputErrors.predefinedCommand ? trans(inputErrors.predefinedCommand) : undefined,
            },
            customCommand: {
                label   : capitalizeAll(trans('label.custom_command')),
                editable: true,
                required: true,
                hidden  : selectedPredefinedCommand !== CUSTOM_COMMAND_OPTION,  // Hide this input field if selected command is not CUSTOM command
                value   : currentInputs.customCommand,
                error   : inputErrors.customCommand ? trans(inputErrors.customCommand) : undefined,
            },
            parameters: {
                label      : capitalizeAll(trans('label.parameters')),
                editable   : true,
                required   : true,
                description: trans('input.json.hint_text'),
                hidden     : !hasParametersInput,           // Hide this input field if !hasParametersInput
                value      : currentInputs.parameters,
                error      : inputErrors.parameters ? trans(inputErrors.parameters) : undefined,
            },
            hasResponse: {
                label      : capitalizeAll(trans('label.has_response')),
                editable   : true,
                required   : true,
                description: trans('input.has_response.hint_text'),
                hidden     : selectedPredefinedCommand !== CUSTOM_COMMAND_OPTION,  // Hide this input field if the user selected one of the predefined command
                value      : currentInputs.hasResponse,
            },
        };
    }, [currentInputs.predefinedCommand, currentInputs.customCommand, currentInputs.parameters, currentInputs.hasResponse, props.predefinedCommands,
        trans, commandOptions, inputErrors.predefinedCommand, inputErrors.customCommand, inputErrors.parameters]);


    /**
     * Set up and cache the actions for the SendDeviceCommandComp
     */
    const actionsSettings = useMemo(() => {
        return {
            startEdit: {
                onClick: () => {
                    onChangeEditingMode(true);
                },
            },
            cancelEdit: {
                label  : capitalizeAll(trans('label.close')),
                onClick: onCancelEdit,
            },
            saveUpdate: {
                label  : capitalizeAll(trans('label.send')),
                onClick: onSendDeviceCommandFormSubmit,
            },
            // If the "parameters" input is enabled then show the "Format JSON" button
            formatJson: !inputsSettings.parameters?.hidden ? {
                label  : capitalizeAll(trans('label.format_json')),
                onClick: onFormatParametersJSON,
            } : undefined,
        };
    }, [inputsSettings.parameters?.hidden, onCancelEdit, onChangeEditingMode, onFormatParametersJSON, onSendDeviceCommandFormSubmit, trans]);



    return (
        <SendDeviceCommandComp
            className={clsx(props.className, props.infoCompProps?.className, isEditing && 'editing')}
            formProps={props.infoCompProps?.formProps}
            isEditing={isEditing}
            title={capitalizeAll(trans('label.send_command_to_device'))}
            icon="send_to_mobile"
            fields={inputsSettings}
            resultData={lastCommandResult}
            actions={actionsSettings}
            onFieldClicked={() => {
                onChangeEditingMode(true);
            }}
            texts={{
                resultLabel: capitalizeAll(trans('label.result')),
            }}
            onFormInputChange={onSendCommandCompFormInputChange}
        />
    );
};
